import type { CallbackInterface } from 'recoil'

import { logAppError } from 'Helpers/logger'
import { errorUpdater, loaderUpdater, pageState } from 'State/page'
import type { PageStateType } from 'Types/state'

import type { SearchState } from 'Pages/GroupsOverlap/model'
import {
  resultsState,
  searchFromCounterState,
  searchState,
} from 'Pages/GroupsOverlap/recoil/state/state'
import {
  getGroupsOverlap,
  getRecipientsCount,
} from 'Pages/GroupsOverlap/services/services'

export const initPageCallback =
  ({ set, reset }: CallbackInterface) =>
  async () => {
    set<PageStateType>(pageState, loaderUpdater(true))

    try {
      set<PageStateType>(pageState, (prev) => ({ ...prev, status: 'ready' }))
    } catch (error) {
      console.error('### error', error)

      logAppError({
        Component: 'GroupsOverlap',
        Method: 'initPageCallback',
        appError: error,
      })

      set<PageStateType>(pageState, errorUpdater)
    }

    set<PageStateType>(pageState, loaderUpdater(false))
  }

export const resetPageCallback =
  ({ set, reset }: CallbackInterface) =>
  async () => {
    reset(searchState)
    reset(searchFromCounterState)
    reset(resultsState)
  }

export const errorPageCallback =
  ({ set }: CallbackInterface) =>
  async (error: Error) => {
    logAppError({
      Component: 'GroupsOverlap',
      Method: 'errorPageCallback',
      appError: error,
    })

    set<PageStateType>(pageState, errorUpdater)
  }

export const changeSearchCallback =
  ({ reset, set }: CallbackInterface) =>
  async (p: Partial<SearchState>) => {
    set(searchState, (prev) => ({
      ...prev,
      ...p,
    }))

    if ('from' in p) {
      if (p.from === undefined) {
        reset(searchFromCounterState)
      } else {
        set(searchFromCounterState, { status: 'loading' })
        try {
          const count = await getRecipientsCount(p.from)
          set(searchFromCounterState, { status: 'ready', count })
        } catch (error) {
          set(searchFromCounterState, { status: 'error' })
        }
      }
    }
  }

export const searchCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async () => {
    const { from, to } = await snapshot.getPromise(searchState)
    if (from !== undefined) {
      set(resultsState, {
        status: 'loading',
      })

      try {
        const items = await getGroupsOverlap(from, to)

        set(resultsState, {
          status: 'ready',
          items,
        })
      } catch (error) {
        console.error('### error', error)

        logAppError({
          Component: 'GroupsOverlap',
          Method: 'searchCallback',
          appError: error,
        })

        set<PageStateType>(pageState, errorUpdater)
      }
    }
  }
